import { client } from '@utils/graphql/setup';
import {
  QueryCreator,
  CriteriaCreator,
  MutationCreator,
  SingleRecordMutationCreator,
} from './base';
import { createAcceptedProperties } from '../helpers';
import { createMockDevice } from '../../../../tests/unittests/utils/mocks/devices';
import { faker } from '@faker-js/faker';
import * as is from 'is';
import { deleteStream, StreamsQuery } from './streams';
import { dataRegex } from '../helpers';
import { deleteSource, SourcesQuery } from './sources';
import { getLogger } from '@utils/general';
const logger = getLogger('src.utils.graphQL.creators.device');

const acceptedReturnProperties = createAcceptedProperties(
  'id',
  'name',
  'type',
  'connection_type',
  'description',
  'network_address',
  'latitude',
  'longitude',
  'elevation',
  'geo_json',
  'username',
  'has_creds',
  'problem_camera',
  'metadata',
  'streams.id',
  'streams.name',
  'streams.online',
  'sources.id',
  'sources.name',
  'streams.clspsAddress',
  'streams.zone.name',
  'streams.appliance.id',
  'streams.appliance.name',
  'streams.appliance.public_address',
  'streams.appliance.management_address',
  'streams.source.id',
  'streams.source.name',
  'streams.source.device.id',
  'streams.source.device.name',
  'streams.source.appliance.id',
  'streams.source.appliance.name'
);
const acceptedCriteriaProperties = {
  name: { type: 'string' },
  type: { type: 'enum' },
  description: { type: 'string', triplequoted: true },
  network_address: { type: 'string' },
  latitude: { type: 'number' },
  longitude: { type: 'number' },
  elevation: { type: 'number' },
  username: { type: 'string' },
  password: { type: 'string' },
  connection_type: { type: 'enum' },
  problem_camera: { type: 'boolean' },
  latitudeText: { type: 'string' },
  longitudeText: { type: 'string' },
  or: { type: 'or' },
  and: { type: 'and' },
  status: { type: 'number' },
  not: { type: 'not' },
  metadata_filter: { type: 'metadata_filter' },
};

class DeviceCriteria extends CriteriaCreator {
  constructor(criteria = {}) {
    super(criteria);
  }
  get acceptedCriteria() {
    return acceptedCriteriaProperties;
  }
}

export class DevicesQuery extends QueryCreator {
  #streamQuery = null;
  #sourceQuery = new SourcesQuery('sources', { includeDeviceQuery: false });
  #includeMetadata = false;
  #includePrivateMetadata = false;

  constructor(queryType = 'devices', id = null, options = {}) {
    super(queryType, DeviceCriteria);
    this.#streamQuery = new StreamsQuery('streams', null, { includeSourceQuery: !!options?.includeSourceQuery });
    this.#includePrivateMetadata = !!options?.includePrivateMetadata;

    // Set ID if provided
    if (id) {
      this.setId(id);
    }

    // Set pagination if provided
    if (options?.pagination) {
      const {
        count_per_page, current_page, start_on_page,
      } = options.pagination;
      if (count_per_page) this.addPagination('countPerPage', count_per_page);
      if (start_on_page) this.addPagination('startOnPage', start_on_page);
      if (current_page) this.addPagination('startOnPage', current_page); // alias for current_page
    }
    // Always add pagination return fields
    this.addPaginationReturn('total_pages', 'total_records', 'count_per_page', 'current_page');

    // Set sort if provided
    if (options?.sort) {
      this.setSort(options.sort.field, options.sort.direction);
    }
  }

  get acceptedReturnProperties() {
    return acceptedReturnProperties;
  }

  addReturnProperties(...properties) {
    for (const property of properties) {
      if (property === 'metadata') {
        this.#includeMetadata = true;
        continue;
      }

      if (property.startsWith('streams')) {
        const [, ...streamProperty] = property.split('.');
        this.#streamQuery.addReturnProperties(streamProperty.join('.'));
        continue;
      }

      if (property.startsWith('sources')) {
        const [, ...sourceProperty] = property.split('.');
        this.#sourceQuery.addReturnProperties(sourceProperty.join('.'));
        continue;
      }

      super.addReturnProperties(property);
    }
    return this;
  }
  returnSyntax() {
    const syntax = super.returnSyntax();
    if (syntax === '') {
      return syntax;
    }

    const [, data] = syntax.match(dataRegex);
    const streamSyntax = this.#streamQuery.returnSyntax();
    const sourceSyntax = this.#sourceQuery.returnSyntax();

    const dataSyntax = [data.trim()];

    if (this.#includeMetadata) {
      if (this.#includePrivateMetadata) {
        dataSyntax.push('metadata(include_private: true) {\nid\nkey\nvalue\nprivate\n}');
      } else {
        dataSyntax.push('metadata {\nid\nkey\nvalue\nprivate\n}');
      }
    }

    if (!is.empty(streamSyntax)) {
      dataSyntax.push(streamSyntax.replace(/^data/, 'streams').replace(/\n$/, '').trim());
    }

    if (!is.empty(sourceSyntax)) {
      dataSyntax.push(
        sourceSyntax.replace(/^data/, 'sources').replace(/\n$/, '').trim()
      );
    }
    return `data {\n${dataSyntax.join('\n')}\n}`;
  }
}

export class SingleDeviceMutation extends SingleRecordMutationCreator {
  constructor(queryType = 'create_device', criteria = {}) {
    super(queryType, DeviceCriteria);
    this.setCriteria(criteria);
  }
  get acceptedReturnProperties() {
    return acceptedReturnProperties;
  }
}

export function getDevices(id, criteria = {}, options = {}) {
  let query;
  if (id) {
    query = new DevicesQuery('device', id, options);
  } else {
    query = new DevicesQuery('devices', null, options);
    if (!is.empty(criteria)) {
      Object.keys(criteria).forEach((key) => {
        query.addCriteria(key, criteria[key]);
      });
    }
  }

  return {
    query,
    execute() {
      return /^dev(?:elop(?:ment)?)?$/i.test(import.meta.env.VITE_ENV)
        ? Promise.resolve().then(() => {
          if (id) {
            return { data: createMockDevice() };
          }
          const devices = faker.helpers.multiple(createMockDevice, {
            count: faker.number.int({ min: 5, max: 10 }),
          });
          return { data: devices };
        })
        : client.query(query.name, query.toString());
    },
  };
}

export class DevicesMutation extends MutationCreator {
  constructor(queryType = 'create_device', criteria = {}) {
    super(queryType, DeviceCriteria);
    this.setCriteria(criteria);
  }
  get acceptedReturnProperties() {
    return acceptedReturnProperties;
  }
}

export function saveDevice(mutationType, input) {
  let mutation;
  mutation = new DevicesMutation(mutationType, input);
  return {
    mutation,
    execute() {
      return client.mutate(mutation.name, mutation.toString());
    },
  };
}

export function updateDevice(mutationType, id, input) {
  let mutation;
  mutation = new SingleDeviceMutation(mutationType, input);
  mutation.setId(id);

  return {
    mutation,
    execute() {
      return client.mutate(mutation.name, mutation.toString());
    },
  };
}

export const deleteDevice = async (device) => {
  const { execute } = updateDevice('remove_device', device.id);

  try {
    if (device) {
      for (const stream of device.streams) {
        await deleteStream(stream);
      }

      for (const source of device.sources) {
        await deleteSource(source);
      }
    }
    return await execute();
  }
  catch (error) {
    logger.error(error, `Failed to delete device ${device.id}.`);
    throw new Error(error);
  }
};
